import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import arrow from "../../img/arrow1.png";
import CalendarIcon from "../../img/calendaricon.svg";
import { ReactComponent as EditIcon } from "../../img/editicon.svg";
import { ReactComponent as TrashIcon } from "../../img/trashicon.svg";
import { ReactComponent as PlusIcon } from "../../img/plusicon.svg";
import { instance, instance2 } from "../../utils/axiosInstance";
import { formatNumber } from "../../numberingformat";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/shadcn/dialog";
import { Button } from "src/components/shadcn/button";
import { ChevronLeft } from "lucide-react";
import { set } from "lodash";

export default function PesananScreen() {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [purchaseOrder, setPurchaseOrder] = useState([]);
  const [pOKitchen, setPOKitchen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  //const [selectedDate, setSelectedDate] = useState(new Date());

  const localDateString = (thedate) => {
    const year = thedate.getFullYear();
    const month = String(thedate.getMonth() + 1).padStart(2, "0"); // Adding 1 because months are 0-based
    const day = String(thedate.getDate()).padStart(2, "0");

    // console.log("startDate", thedate);
    const localDateString = `${year}-${month}-${day}`;

    return localDateString;
  };

  const [weekDetails, setWeekDetails] = useState({
    weekNumber: 1,
    startDate: "",
    endDate: "",
  });

  const roleId = parseInt(localStorage.getItem("roleId"), 10);
  const [isOpen, setIsOpen] = useState(false);

  const [locationId, setLocationId] = useState("");
  const [selectedRowData, setSelectedRowData] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [additionalSuppliers, setAdditionalSuppliers] = useState([]);

// Total qty from all supplier splits (main + additional)
const totalQtyFromSuppliers = useMemo(() => {
  if (!selectedRowData) return 0;
  const main = Number(selectedRowData?.supplier_quantity ?? 0) || 0;
  const others = additionalSuppliers.reduce(
    (sum, s) => sum + (Number(s.quantity ?? 0) || 0),
    0
  );
  return main + others;
}, [selectedRowData, additionalSuppliers]);


// Initially show backend total, but once split fields exist they’ll equal backend;
// if user edits, this follows the sum immediately.
const effectiveTotalQty = useMemo(() => {
  const backendTotal = parseInt(selectedRowData?.order_qty ?? 0, 10) || 0;
  // If you want to *always* reflect supplier sum, just return totalQtyFromSuppliers.
  // Keeping backendTotal for safety if supplier fields are empty/null:
  return totalQtyFromSuppliers || backendTotal;
}, [selectedRowData, totalQtyFromSuppliers]);
// --- formatting helpers (adjust for decimals if needed) ---
const onlyDigits = (s) => s.replace(/[^\d]/g, ""); // keep digits only
const fmt = (n) => formatNumber(n ?? 0);           // your existing formatter
// Tolerant parse: accepts "1.234,56", "1,5", "1234.56", "1000" -> returns numeric 1234.56 or 1.5 etc.
const parseNumberFromInput = (s) => {
  if (s === null || s === undefined) return 0;
  const str = String(s).trim();
  if (str === "") return 0;
  let cleaned = str.replace(/\s/g, "");

  // If both '.' and ',' present: assume '.' thousands and ',' decimal
  if (cleaned.includes(".") && cleaned.includes(",")) {
    cleaned = cleaned.replace(/\./g, "").replace(",", ".");
  } else if (cleaned.includes(",") && !cleaned.includes(".")) {
    // "1,5" -> "1.5"
    cleaned = cleaned.replace(",", ".");
  } else {
    // Only dots or only digits: remove non-digit except dot/minus.
    cleaned = cleaned.replace(/[^\d.-]/g, "");
    // If multiple dots, treat last as decimal:
    const parts = cleaned.split(".");
    if (parts.length > 2) {
      const last = parts.pop();
      cleaned = parts.join("") + "." + last;
    }
  }

  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
};

/**
 * Format an input-like string for display:
 * - if there's a decimal separator (',' or '.' interpreted as decimal), preserve decimal part and format integer with thousand dots.
 * - otherwise format as integer with thousand dots.
 * Examples:
 *  "1,5" -> "1,5"
 *  "1500" -> "1.500"
 *  "1234.56" -> "1.234,56"
 */
const formatInputValue = (raw) => {
  raw = String(raw ?? "");

  // keep original to check trailing chars
  const original = raw;

  // Trim spaces but preserve trailing separators if they exist at the end
  raw = raw.replace(/\s/g, "");

  if (raw === "") return "";

  // keep only digits, dot, comma and minus for processing
  const onlyAllowed = raw.replace(/[^\d.,-]/g, "");

  // detect if user ended with a decimal separator (`,` or `.`)
  const endsWithComma = original.endsWith(",");
  const endsWithDot = original.endsWith(".");

  // If there's a comma anywhere -> treat comma as decimal separator (id style)
  if (onlyAllowed.includes(",")) {
    // remove thousand dots user may have typed
    const cleaned = onlyAllowed.replace(/\./g, "");
    const [intPart = "", decPart = ""] = cleaned.split(",", 2);
    const intDigits = (intPart.replace(/^0+(?=\d)/, "") || "0");
    const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    // If the original input ended with a comma, preserve the comma (user is starting fractional part)
    if (endsWithComma) {
      return `${formattedInt},`;
    }
    return decPart !== "" ? `${formattedInt},${decPart}` : formattedInt;
  }

  // No comma present: maybe dot is decimal, maybe dot are thousand separators.
  if (onlyAllowed.includes(".")) {
    const lastDotIndex = onlyAllowed.lastIndexOf(".");
    const digitsAfter = onlyAllowed.length - lastDotIndex - 1;

    // treat last dot as decimal if 1-2 digits after it (common decimal typing)
    if (digitsAfter > 0 && digitsAfter <= 2) {
      // convert last dot to comma for display
      const withMarker = onlyAllowed.replace(/\.(?=[^.]*$)/, "<<<DOT>>>");
      const removedOtherDots = withMarker.replace(/\./g, "");
      const converted = removedOtherDots.replace("<<<DOT>>>", ",");
      const [intPart = "", decPart = ""] = converted.split(",", 2);
      const intDigits = (intPart.replace(/^0+(?=\d)/, "") || "0");
      const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
      // If user ended with dot, preserve as comma
      if (endsWithDot) {
        return `${formattedInt},`;
      }
      return decPart !== "" ? `${formattedInt},${decPart}` : formattedInt;
    }

    // otherwise treat dots as thousand separators -> remove them and format integer
    const onlyDigits = onlyAllowed.replace(/\./g, "");
    const intDigits = (onlyDigits.replace(/^0+(?=\d)/, "") || "0");
    const formattedInt = intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    return formattedInt;
  }

  // Only digits -> format thousands
  const onlyDigits = onlyAllowed.replace(/\D/g, "");
  const intDigits = (onlyDigits.replace(/^0+(?=\d)/, "") || "0");
  return intDigits.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
};


/**
 * Format a numeric (or numeric-string) value for display in input using Indonesian style:
 * thousands '.' and decimal ',' up to 2 decimals (don't round decimals more than 2 by default).
 */
const formatQtyDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  const hasFraction = Math.abs(n % 1) > 0;
  const opts = {
    minimumFractionDigits: hasFraction ? 0 : 0,
    maximumFractionDigits: 2,
  };
  // Use id-ID to produce "1.234,56"
  return new Intl.NumberFormat("id-ID", opts).format(n);
};
// Unit price raw (from selected row)

// 1) Make raw derive directly without the `|| 0` truthiness step.
const unitPriceRaw = useMemo(() => {
  return Number(selectedRowData?.price_unit ?? 0);
}, [selectedRowData?.price_unit]);

// 2) State for raw + formatted input
const [unitPrice, setUnitPrice] = useState(0);
const [unitPriceInput, setUnitPriceInput] = useState("0");


// 5) On change, keep raw in state and show formatted
const onChangeUnitPrice = (e) => {
  const rawStr = e.target.value.replace(/[^\d]/g, "");
  const rawNum = rawStr ? parseInt(rawStr, 10) : 0;
  setUnitPrice(rawNum);
  setUnitPriceInput(formatNumber(rawNum));
};

// Keep input string in sync when source data changes
useEffect(() => {
  setUnitPrice(unitPriceRaw);
  setUnitPriceInput(unitPriceRaw ? fmt(unitPriceRaw) : "");
}, [unitPriceRaw]);

// Recompute total when raw changes
const [totalPrice, setTotalPrice] = useState(0);
useEffect(() => {
  setTotalPrice(unitPrice * effectiveTotalQty);
}, [unitPrice, effectiveTotalQty]);


const handleAddSupplier = () => {
  setAdditionalSuppliers((prev) => [
    ...prev,
    { supplier_id: null, quantity: null, quantity_display: "" },
  ]);
};


  const handleAdditionalSupplierChange = (value, index) => {
    const updatedSuppliers = [...additionalSuppliers];
    updatedSuppliers[index].supplier_id = parseInt(value, 10) || null;
    setAdditionalSuppliers(updatedSuppliers);
  };

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await instance2.get(`supplier`); // Use instance2 for the API call
        setSuppliers(response.data); // Axios automatically parses JSON, accessing response.data directly
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    };

    fetchSuppliers();
  }, []);

  useEffect(() => {
    const storedLocationId = localStorage.getItem("locationId");

    if (storedLocationId && !isNaN(storedLocationId)) {
      const parsedId = parseInt(storedLocationId, 10);
      setLocationId(parsedId);
    }
  }, []);

  // Access state from the navigate call
  const location = useLocation();

  // Extract startDate and endDate from the URL query string
  const queryParams = new URLSearchParams(location.search);
  const startDate = queryParams.get("startDate");
  const endDate = queryParams.get("endDate");
  const kitchenId = queryParams.get("kitchenId");
  //const selectedDate = startDate;

  const locId = localStorage.getItem("locationId");

  useEffect(() => {
    const fetchPurchaseOrder = async () => {
      try {
        const response = await instance2.get(
          `purchase_order/location/${locId}/${startDate}/${endDate}`,
        ); // Use instance2 for the API call
        setPurchaseOrder(response.data); // Axios automatically parses JSON, accessing response.data directly
        //console.log("goooo1");
        setPOKitchen(
          response.data.filter((item) => item.kitchen_id === kitchenId),
        );
      } catch (error) {
        console.error("Error fetching purchase order:", error);
      }
    };

    fetchPurchaseOrder();
  }, [startDate, endDate, locId]);

  if (pOKitchen.length > 0) {
    //console.log("goooo2");
    navigate(`/daftarpemesanan?startDate=${startDate}&endDate=${endDate}`);
  } else {
    //console.log("goooo3",purchaseOrder.length)
    //navigate(`/draftOrder`)
  }

  const getWeekStartAndEndDates = (date) => {
    const current = new Date(date);
    const dayOfWeek = current.getDay(); // Get the day of the week (0 - Sunday, 6 - Saturday)

    // Calculate Monday (start of the week)
    const monday = new Date(current);
    monday.setDate(current.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));

    // Calculate Friday (end of the week)
    const friday = new Date(monday);
    friday.setDate(monday.getDate() + 4);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);

    return { startDate: monday, endDate: sunday };
  };

  const fetchData = useCallback(async () => {
    if (!locationId) {
      console.log("Location ID not yet available.");
      return;
    }

    setLoading(true);
    setIsPopupVisible(true);

    try {
      const url = `ingredient_location/${locationId}/${localDateString(
        new Date(startDate),
      )}/${localDateString(new Date(endDate))}`;

      console.log("Requested URL:", url);
      const response = await instance2.get(url);

      let sortedData = [...response.data].sort((a, b) => {
        if (a.kitchen_id !== b.kitchen_id) {
          return a.kitchen_id - b.kitchen_id;
        }
        return new Date(a.date) - new Date(b.date);
      });

      if (kitchenId) {
        sortedData = sortedData.filter(
          (item) => item.kitchen_id === parseInt(kitchenId, 10),
        );
      }

      setData(sortedData);

      if (response.data.length === 0) {
        console.warn("No data found.");
        // Optional: handle no-data case
      }
    } catch (err) {
      console.error("Fetch error:", err);
      setError(err.response?.data?.message || "Failed to fetch data");
    } finally {
      setLoading(false);
      setIsPopupVisible(false);
    }
  }, [locationId, kitchenId, startDate, endDate]);

  // ✅ Call it on mount and when dependencies change
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groupedData = data.reduce((acc, item) => {
    const date = localDateString(new Date(item.date)); // Extract the date part
    if (!acc[date]) {
      acc[date] = [];
    }
    acc[date].push(item);
    return acc;
  }, {});

  const getWeekDetails = (date) => {
    console.log("Input date:", date);

    // Ensure the input is converted to a Date object if it's not already
    const currentDate = startDate ? new Date(startDate) : new Date(date);
    if (isNaN(currentDate)) {
      console.error("Invalid date provided:", date);
      return {
        weekNumber: null,
        monthName: null,
        startDate: null,
        endDate: null,
      };
    }

    console.log("Parsed date:", currentDate);

    const startOfMonth = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      1,
    ); // Start of the month
    const dayOfWeek = currentDate.getDay();

    // Calculate the start of the week (Monday)
    //const startDate = new Date(currentDate);
    const diff = startDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1); // Monday
    startDate.setDate(diff);

    console.log("Calculated startDate:", startDate);

    // Calculate the end of the week (Friday)
    const endDate = new Date(startDate);
    //endDate.setDate(startDate.getDate() + 4); // Friday is 4 days after Monday
    endDate.setDate(startDate.getDate() + 6); // Sunday is 4 days after Monday
    console.log("Calculated endDate:", endDate);

    // Calculate the week number of the month
    const daysSinceStartOfMonth = Math.floor(
      (startDate - startOfMonth) / (1000 * 60 * 60 * 24),
    );
    const weekNumber = Math.ceil((daysSinceStartOfMonth + 1) / 7);
    console.log("Week number:", weekNumber);

    // Month Names
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const monthName = monthNames[startDate.getMonth()]; // Use startDate for the month name
    console.log("Month name:", monthName);

    const localDateString = (thedate) => {
      const year = thedate.getFullYear();
      const month = String(thedate.getMonth() + 1).padStart(2, "0"); // Adding 1 because months are 0-based
      const day = String(thedate.getDate()).padStart(2, "0");

      // console.log("startDate", thedate);
      const localDateString = `${year}-${month}-${day}`;

      return localDateString;
    };

    return {
      weekNumber,
      monthName,
      startDate: localDateString(startDate),
      endDate: localDateString(endDate),
    };
  };

  const handleQuantityChange = (index, value) => {
    const updatedSuppliers = [...additionalSuppliers];
    updatedSuppliers[index].quantity = parseInt(value, 10) || 0;
    setAdditionalSuppliers(updatedSuppliers);
  };

  const enrichedSelectedRowData = selectedRowData
    ? {
        ...selectedRowData,
        supplier_quantity:
          selectedRowData.supplier_quantity || selectedRowData.order_qty,
      }
    : null;

  const enrichedAdditionalSuppliers = additionalSuppliers.map((supplier) => ({
    ...supplier,
    quantity: supplier.quantity || 0,
  }));

  useEffect(() => {
    // Other effects that don't modify state
  }, [enrichedSelectedRowData, enrichedAdditionalSuppliers]);

  const totalSupplierQuantity = () => {
    if (!selectedRowData) return 0;

    return (
      parseInt(selectedRowData.supplier_quantity || 0, 10) +
      additionalSuppliers.reduce(
        (acc, supplier) => acc + parseInt(supplier.quantity || 0, 10),
        0,
      )
    );
  };

  // Update week details when the selected date changes
  // useEffect(() => {
  //     const { weekNumber, startDate, endDate } = getWeekDetails(startDate);
  //     setWeekDetails({ weekNumber, startDate, endDate });
  //   }, [selectedDate]);

  //   const CustomInput = React.forwardRef(({ onClick, isOpen }, ref) => (
  //     <button
  //       className="flex items-center justify-between w-full px-4 py-2 border-none rounded-[15px] bg-white"
  //       onClick={onClick}
  //       ref={ref}
  //       type="button"
  //     >
  //       {/* Left Section: Calendar Icon */}
  //       <div className="flex items-center gap-2">
  //       <img src={CalendarIcon} alt="Calendar" className="h-5 w-5 text-gray-500" />

  //   {/* Separator */}
  //   <div className="w-px h-5 bg-gray-300 mx-1 me-3"></div>
  //         <span>
  //           {new Date(selectedDate).toLocaleDateString("id-ID", {
  //             year: "numeric",
  //             month: "long",
  //             day: "2-digit",
  //           })}
  //         </span>
  //       </div>

  //       {/* Right Section: Rotating Arrow */}
  //       <div
  //       className={`text-black font-semibold transform transition-transform duration-500 ease-in-out ps-4 ${
  //         isOpen ? "rotate-180" : "rotate-0"
  //       }`}
  //     >
  //         ▼
  //       </div>
  //     </button>
  //   ));

  const [isFormVisible, setIsFormVisible] = useState(false);
  const formRef = useRef(null); // Create a ref for the form container

  useEffect(() => {
    if (isFormVisible && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isFormVisible]); // Trigger when isFormVisible changes

  //console.log(data);

  const handleEditClick = (
    id,
    locationId,
    kitchen_id,
    ingredientId,
    startDate,
    endDate,
  ) => {
    try {
      // Find all rows that belong to the same group
      const result = data.filter(
        (item) =>
          item.kitchen_id === kitchen_id &&
          item.ingredient_id === ingredientId &&
          localDateString(new Date(item.date)) ===
            localDateString(new Date(startDate)),
      );

      if (result.length === 0) {
        console.warn("No matching data group found.");
        return;
      }

      const totalOrderQty = result.reduce(
        (acc, item) => acc + parseInt(item.order_qty || 0, 10),
        0,
      );

      // First supplier goes in main form
// when setting main selectedRowData
setSelectedRowData({
  date: localDateString(new Date(result[0].date)),
  ingredient_id: result[0].ingredient_id,
  ingredient_name: result[0].ingredient_name,
  kitchen_id: result[0].kitchen_id,
  kitchen_name: result[0].kitchen_name,
  header_id: result[0].header_id,
  multiple_qty: result[0].multiple_qty,
  required_qty: result[0].required_qty,
  order_qty: totalOrderQty,
  supplier_quantity: parseNumberFromInput(String(result[0].order_qty)), // numeric
  supplier_quantity_display: formatInputValue(String(result[0].order_qty)), // display
  supplier_id: result[0].supplier_id,
  price_total: result[0].price_total,
  price_unit: result[0].price_unit,
});

// additional suppliers: map to include quantity_display
const additionalSuppliers = result.map((item) => ({
  supplier_id: item.supplier_id,
  quantity: parseNumberFromInput(String(item.order_qty)),
  quantity_display: formatInputValue(String(item.order_qty)),
}));

setAdditionalSuppliers(additionalSuppliers.slice(1));


      if (formRef.current) {
        formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      setIsFormVisible(true);
    } catch (error) {
      console.error("Error setting up form for editing:", error);
    }
  };

  const getSelectedSuppliers = () => {
    const mainSupplier = selectedRowData?.supplier_id
      ? [selectedRowData.supplier_id]
      : [];
    const additionalSelected = additionalSuppliers
      .map((supplier) => supplier.supplier_id)
      .filter((id) => id != null);
    return [...mainSupplier, ...additionalSelected];
  };

  useEffect(() => {
    const initialSelectedSuppliers = [];

    // Include the first supplier if selected
    if (selectedRowData?.supplier_id) {
      initialSelectedSuppliers.push(selectedRowData.supplier_id);
    }

    // Include additional suppliers if selected
    additionalSuppliers.forEach((supplier) => {
      if (supplier.supplier_id) {
        initialSelectedSuppliers.push(supplier.supplier_id);
      }
    });

    setSelectedSuppliers(initialSelectedSuppliers);
  }, [selectedRowData, additionalSuppliers]);

  const [selectedSuppliers, setSelectedSuppliers] = useState([]); // State to track selected supplier IDs

  const adminId = localStorage.getItem("userAuthentication");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault(); // Optional: Comment this if native refresh is desired

    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      if (
        !locationId ||
        !selectedRowData ||
        !selectedRowData.date ||
        !adminId
      ) {
        alert("Please ensure all required fields are filled out.");
        setIsSubmitting(false);
        return;
      }

const collectedSuppliers = [
  {
    supplier_id: selectedRowData.supplier_id,
    quantity: selectedRowData.supplier_quantity,
  },
  ...additionalSuppliers.map((supplier) => ({
    supplier_id: supplier.supplier_id,
    quantity: supplier.quantity,
  })),
].filter((supplier) => supplier.supplier_id && supplier.quantity);


      if (collectedSuppliers.length === 0) {
        alert("Please add valid suppliers and quantities.");
        setIsSubmitting(false);
        return;
      }

      const finalOrderDetails = {
        location_id: locationId,
        date: selectedRowData.date,
        ingredient_id: selectedRowData.ingredient_id,
        suppliers: collectedSuppliers.map((supplier) => supplier.supplier_id),
        orders: collectedSuppliers.map((supplier) => supplier.quantity),
        required_qty: selectedRowData.required_qty,
        header_id: selectedRowData.header_id,
        kitchen_id: selectedRowData.kitchen_id,
        multiple_qty: selectedRowData.multiple_qty,
        admin_id: adminId,
        price_unit: unitPrice,
        price_total: totalPrice,
      };

      console.log("Final Payload:", finalOrderDetails);

      const response = await instance2.post("order_detail", finalOrderDetails);
      console.log("Order posted successfully:", response.data);

      setSelectedRowData([]);
      setAdditionalSuppliers([]);
      setIsFormVisible(false);

      // Optional: Force page reload
      fetchData();
    } catch (error) {
      console.error(
        "Error posting order details:",
        error.response?.data || error.message,
      );
      alert("Failed to submit order details.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isFormValid = () => {
    // Check if `selectedRowData` exists and has valid `supplier_id` and `supplier_quantity`
    if (
      !selectedRowData ||
      !selectedRowData.supplier_id ||
      selectedRowData.supplier_quantity == null ||
      selectedRowData.supplier_quantity === 0
    ) {
      return false;
    }

    // Validate all additional suppliers
    for (const supplier of additionalSuppliers) {
      if (
        !supplier.supplier_id ||
        supplier.quantity == null ||
        supplier.quantity === 0
      ) {
        return false;
      }
    }

    // If all checks pass, the form is valid
    return true;
  };

  const handleCreateOrder = async () => {
    try {
      const payload = {
        // Add your payload structure here

        admin_id: localStorage.getItem("userAuthentication") || "",
        location_id: localStorage.getItem("locationId") || "",
        startdate: startDate,
        enddate: endDate,
      };

      const response = await instance2.post("/final_order", payload); // Adjust endpoint to match your API

      if (response.status === "OK") {
        //alert("Order created successfully!");

        // Show loading message
        setLoading(true);
        setIsPopupVisible(true); // Show the popup

        // Wait 5 seconds before navigating
        setTimeout(() => {
          setLoading(false); // Hide loading message
          setIsPopupVisible(false); // Show the popup
          navigate(
            `/daftarpemesanan?startDate=${localDateString(new Date(startDate))}&endDate=${localDateString(new Date(endDate))}`,
          );
        }, 5000);
      } else {
        throw new Error("Failed to create order.");
      }
    } catch (error) {
      console.error(
        "Error creating order:",
        error.message || error.response?.data,
      );
      alert("Failed to create order. Please try again.");
    }
  };

  const handleCloseEdit = () => {
    setIsFormVisible(false);
  };

  const allItemsHaveSupplier = useMemo(() => {
    return data.every((item) => item.supplier_id);
  }, [data]);

  // useEffect(() => {
  //   const getNextMonday = () => {
  //     const currentDate = new Date();
  //     const dayOfWeek = currentDate.getDay();
  //     const daysUntilNextMonday = (8 - dayOfWeek) % 7;
  //     currentDate.setDate(currentDate.getDate() + daysUntilNextMonday);
  //     currentDate.setHours(0, 0, 0, 0);

  //     return currentDate;
  //   };

  //   // If roleId is 2, set selected date to next Monday, else this week's Monday
  //   setSelectedDate(roleId === 2 ? getNextMonday() : new Date());
  // }, [roleId]);
  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [popupMessage, setPopupMessage] = useState("");

  const isAuthorized = [2, 4, 5].includes(roleId);
  const isButtonVisible = startDate && !loading;
  const isButtonEnabled = isButtonVisible && allItemsHaveSupplier;

// 3) Always write both states on change of the edited row (id/date)
useEffect(() => {
  // A stable key that changes whenever you pick a different line to edit
  const newRaw = Number(selectedRowData?.price_unit ?? 0);
  setUnitPrice(newRaw);
  // IMPORTANT: remove the truthy check — we want "0" to show as "0"
  setUnitPriceInput(formatNumber(newRaw));
}, [selectedRowData?.ingredient_id, selectedRowData?.date, selectedRowData?.price_unit]);

// 4) Recompute total whenever raw qty/price change
useEffect(() => {
  setTotalPrice(unitPrice * effectiveTotalQty);
}, [unitPrice, effectiveTotalQty]);

// Disable submit jika ada dropdown pemasok yang belum dipilih
const allSuppliersChosen = useMemo(() => {
  if (!isFormVisible) return true; // di luar mode edit, abaikan
  if (!selectedRowData?.supplier_id) return false; // pemasok utama wajib ada
  // semua baris pemasok tambahan juga wajib terpilih
  return additionalSuppliers.every((s) => !!s.supplier_id);
}, [isFormVisible, selectedRowData?.supplier_id, additionalSuppliers]);

// Valid if every supplier dropdown is chosen AND every quantity > 0
const canSubmitForm = useMemo(() => {
  if (!isFormVisible) return true; // outside edit mode, ignore

  // main supplier must exist and qty > 0
  const mainOk =
    !!selectedRowData?.supplier_id &&
    Number(selectedRowData?.supplier_quantity) > 0;

  if (!mainOk) return false;

  // every additional supplier must have id and qty > 0
  for (const s of additionalSuppliers) {
    if (!s?.supplier_id || !(Number(s?.quantity) > 0)) return false;
  }

  return true;
}, [
  isFormVisible,
  selectedRowData?.supplier_id,
  selectedRowData?.supplier_quantity,
  additionalSuppliers,
]);


  return (
    <div className="flex flex-col gap-6 p-4">
      {isFormVisible && selectedRowData && (
        <div ref={formRef} className="mt-8 p-4 rounded-lg">
          <div className="flex flex-row justify-between">
            <h2 className="text-xl font-bold mb-4">Ubah Bahan Baku</h2>
            <PlusIcon
              className="text-merah-no hover:text-merah-hover cursor-pointer transform rotate-45"
              onClick={handleCloseEdit}
            />
          </div>
          <form>
            <div className="flex flex-col lg:flex-row gap-4 md:gap-4 lg:gap-8 text-xxs md:text-xxs lg:text-base">
              {/* Left Column */}
              <div className="flex flex-col flex-1 gap-4">
                {/* Date */}
                <div className="flex flex-row items-center gap-4">
                  <label className="block text-gray-700 w-1/4">Tanggal</label>
                  <input
                    type="text"
                    value={
                      selectedRowData.date
                        ? new Intl.DateTimeFormat("id-ID", {
                            day: "2-digit",
                            year: "numeric",
                            month: "long",
                          })
                            .format(new Date(selectedRowData.date))
                            .replace(",", "")
                            .split(" ")
                            .join(" ")
                        : ""
                    }
                    className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
                    readOnly
                  />
                </div>

                {/* Ingredient Name */}
                <div className="flex flex-row items-center gap-4">
                  <label className="block text-gray-700 w-1/4">Dapur</label>
                  <input
                    type="text"
                    value={selectedRowData.kitchen_name || ""}
                    className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
                    readOnly
                  />
                </div>

                {/* Ingredient Name */}
                <div className="flex flex-row items-center gap-4">
                  <label className="block text-gray-700 w-1/4">
                    Nama Bahan
                  </label>
                  <input
                    type="text"
                    value={selectedRowData.ingredient_name || ""}
                    className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
                    readOnly
                  />
                </div>

                {/* Jumlah Total */}
<div className="flex flex-row items-center gap-4">
  <label className="block text-gray-700 w-1/4">Jumlah Total</label>
  <input
    type="text"
    value={formatNumber(effectiveTotalQty)}
    className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
    readOnly
  />
</div>

{/* Total Harga */}
<div className="flex flex-row items-center gap-4">
<div className="flex flex-col flex-1">
  <label className="block text-gray-700">Harga Per Unit</label>
  <input
    type="text"
    inputMode="numeric"
    value={`Rp. ${unitPriceInput}`}
    onChange={onChangeUnitPrice}
    // onFocus={onFocusUnitPrice}
    // onBlur={onBlurUnitPrice}
    className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
    placeholder="0"
  />
</div>
<div className="flex flex-col flex-1">
  <label className="block text-gray-700">Harga Total</label>
  <input
    type="text"
    value={`Rp. ${fmt(totalPrice)}`}
    readOnly
    className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
  />
</div>
</div>


              </div>

              {/* Supplier Dropdowns and Quantities */}
              <div className="flex flex-col flex-1 gap-4"> {/* smaller vertical gap */}
  {/* Main Supplier */}
  <div className="grid grid-cols-[1.5rem,1fr,1fr,2rem] items-center gap-2">
    <span className="text-gray-700 text-right pr-1">1</span>

   <select
  value={selectedRowData.supplier_id || ""}
  onChange={(e) => {
    const newSupplierId = parseInt(e.target.value, 10);

    // (Optional) enforce uniqueness: if an additional row already uses this supplier, clear it there
    setAdditionalSuppliers(prev => prev.map(s =>
      s.supplier_id === newSupplierId ? { ...s, supplier_id: null } : s
    ));

    setSelectedRowData((prev) => ({
      ...prev,
      supplier_id: newSupplierId,
    }));
  }}
  className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
>
  <option value="" disabled>Pilih Pemasok</option>

  {suppliers
    .filter(opt =>
      // allow if not already chosen anywhere, or it's the current main selection
      !getSelectedSuppliers().includes(opt.id) ||
      opt.id === selectedRowData.supplier_id
    )
    .map(opt => (
      <option key={opt.id} value={opt.id}>
        {opt.supplier_name}
      </option>
    ))}
</select>

<input
  type="text"
  placeholder="Jumlah"
  inputMode="decimal"
  value={
    selectedRowData && (selectedRowData.supplier_quantity_display !== undefined)
      ? selectedRowData.supplier_quantity_display
      : selectedRowData && selectedRowData.supplier_quantity != null
      ? formatInputValue(String(selectedRowData.supplier_quantity))
      : ""
  }
onChange={(e) => {
  const raw = e.target.value;
  const formatted = formatInputValue(raw);
  const parsed = parseNumberFromInput(formatted);
  // main supplier:
  setSelectedRowData(prev => ({
    ...prev,
    supplier_quantity: parsed,
    supplier_quantity_display: formatted,
  }));
}}

onBlur={(e) => {
  const parsed = parseNumberFromInput(e.target.value);
  const display = parsed !== 0 ? formatInputValue(String(parsed)) : "";
  setSelectedRowData(prev => ({
    ...prev,
    supplier_quantity: parsed,
    supplier_quantity_display: display,
  }));
}}

  className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
/>


    {/* empty cell to keep columns aligned with additional rows */}
    <span />
  </div>

  {/* Additional Suppliers */}
  {additionalSuppliers.map((supplier, index) => (
    <div
      key={index}
      className="grid grid-cols-[1.5rem,1fr,1fr,2rem] items-center gap-2"
    >
      <span className="text-gray-700 text-right pr-1">
        {index + 2}
      </span>

      <select
        value={supplier.supplier_id || ""}
        onChange={(e) => handleAdditionalSupplierChange(e.target.value, index)}
        className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
      >
        <option value="" disabled>
          Pilih Pemasok
        </option>
        {suppliers
          .filter(
            (opt) =>
              !getSelectedSuppliers().includes(opt.id) ||
              opt.id === supplier.supplier_id
          )
          .map((opt) => (
            <option key={opt.id} value={opt.id}>
              {opt.supplier_name}
            </option>
          ))}
      </select>
<input
  type="text"
  placeholder="Jumlah"
  inputMode="decimal"
  value={supplier.quantity_display !== undefined ? supplier.quantity_display : (supplier.quantity != null ? formatInputValue(String(supplier.quantity)) : "")}
onChange={(e) => {
  const raw = e.target.value;
  const formatted = formatInputValue(raw);
  const parsed = parseNumberFromInput(formatted);
  // main supplier:
  setSelectedRowData(prev => ({
    ...prev,
    supplier_quantity: parsed,
    supplier_quantity_display: formatted,
  }));
}}

onBlur={(e) => {
  const parsed = parseNumberFromInput(e.target.value);
  const display = parsed !== 0 ? formatInputValue(String(parsed)) : "";
  setSelectedRowData(prev => ({
    ...prev,
    supplier_quantity: parsed,
    supplier_quantity_display: display,
  }));
}}

  className="flex-1 px-3 border-none rounded-[50px] bg-white text-xs py-4"
/>


      <button
        type="button"
        onClick={() => {
          const supplierIdToRemove = supplier.supplier_id;
          setAdditionalSuppliers((prev) => {
            const next = [...prev];
            next.splice(index, 1);
            return next;
          });
          setSelectedSuppliers((prev) =>
            prev.filter((id) => id !== supplierIdToRemove)
          );
        }}
        className="text-gray-500 p-2 rounded hover:text-red-400 flex items-center justify-center"
        aria-label="Hapus pemasok"
      >
        <TrashIcon className="w-4 h-4" />
      </button>
    </div>
  ))}

  {/* Add & Save */}
  <div className="flex justify-center mt-4 mx-4 gap-4">
    <button
      type="button"
      onClick={handleAddSupplier}
      disabled={additionalSuppliers.length + 1 >= suppliers.length}
      className={`flex flex-1 px-3 py-4 outline rounded-[50px] items-center justify-center gap-4 ${
        additionalSuppliers.length + 1 >= suppliers.length
          ? "bg-gray-400 text-gray-700 cursor-not-allowed"
          : "bg-merah-no text-white hover:bg-merah-hover"
      }`}
    >
      <PlusIcon className="w-4 h-4" />
      Tambah Pemasok
    </button>
<button
  onClick={handleSubmit}
  type="submit"
  disabled={!canSubmitForm || isSubmitting}
  className={`flex flex-1 items-center justify-center px-4 py-2 rounded-[50px] w-full
    ${!canSubmitForm || isSubmitting
      ? "bg-gray-400 text-gray-700 cursor-not-allowed"
      : "bg-merah-no text-white hover:bg-merah-hover"}
  `}
>
  Simpan Perubahan
</button>
  </div>

{!canSubmitForm && (
  <p className="text-red-500 text-xs px-4 py-2">
    Pilih pemasok dan isi jumlah untuk semua baris terlebih dulu.
  </p>
)}

</div>

            </div>
          </form>
        </div>
      )}

      <div className="flex lg:flex-row-reverse lg:justify-between lg:items-center flex-col gap-8">
        <Button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 w-full lg:max-w-fit h-fit"
        >
          <ChevronLeft />
          Kembali
        </Button>
        <div className="flex flex-col gap-2">
          <p className="block text-black text-xl sm:text-2xl font-medium">
            Draft Pemesanan Ke Pemasok
          </p>
          {startDate ? (
            <div>
              {`${new Date(startDate).toLocaleDateString("id-ID", {
                year: "numeric",
                month: "long", // For full month name in Indonesian
                day: "2-digit",
              })} - ${new Date(endDate).toLocaleDateString("id-ID", {
                year: "numeric",
                month: "long", // For full month name in Indonesian
                day: "2-digit",
              })}`}
            </div>
          ) : (
            ""
          )}
        </div>
      </div>
      {isAuthorized && (
        <div className="flex flex-col gap-4 items-center text-center w-full ">
          <div className="border-t border-gray-400 w-full flex-1"></div>
          <div className="flex flex-row w-full gap-4 items-center text-center py-4">
            {isButtonVisible && (
              <div className="w-full">
                <button
                  type="button"
                  onClick={handleCreateOrder}
                  disabled={!isButtonEnabled}
                  className={`w-full px-3 py-4 rounded-[50px] uppercase text-xxs lg:text-base transition-colors ${
                    isButtonEnabled
                      ? "bg-merah-no text-white hover:bg-merah-hover"
                      : "bg-gray-400 text-gray-700 cursor-not-allowed"
                  }`}
                >
                  Buat Purchase Order
                </button>
                {!isButtonEnabled && (
                  <p id="supplier-error" className="text-red-500 text-sm mt-2">
                    Terdapat order yang tidak memiliki pemasok. Harap pastikan
                    pemasok untuk setiap order.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}
      <div className="border-t border-gray-400 w-full flex-1"></div>
      {loading && (
        <Dialog
          open={isPopupVisible}
          onOpenChange={(open) => setIsPopupVisible(open)}
        >
          <DialogContent
            className="flex items-center justify-between [&>button.absolute]:hidden"
            onInteractOutside={(e) => e.preventDefault()}
          >
            {/* Left Side - Message */}
            <div className="flex-1 h-full">
              <DialogHeader>
                <DialogTitle>Pemberitahuan</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-justify">{"Mohon Tunggu"}</p>
              <DialogFooter className="sm:justify-end">
                <DialogClose asChild>
                  {/* <Button type="button">Tutup</Button> */}
                </DialogClose>
              </DialogFooter>
            </div>

            {/* Right Side - Spinner */}
            <div className="h-full flex items-center justify-center pl-4">
              <div className="w-10 h-10 border-4 border-gray-300 border-t-merah-no rounded-full animate-spin" />
            </div>
          </DialogContent>
        </Dialog>
      )}
      {error && <p className="text-red-500">{error}</p>}
      {!loading && !error && Object.keys(groupedData).length === 0 && (
        <p>No data available</p>
      )}
      {!loading &&
        !error &&
        Object.entries(groupedData).map(([date, items]) => (
          <div key={date} className="mb-6">
            <h3 className="text-xl font-semibold">
              {new Date(date).toLocaleDateString("id-ID", { weekday: "long" })}
            </h3>
            <h2 className="text-lg font-medium mb-2">
              {new Date(date).toLocaleDateString("id-ID", {
                year: "numeric",
                month: "long",
                day: "2-digit",
              })}
            </h2>
            <table className="w-full">
              <thead>
                <tr className="bg-white">
                  {/* <th className="border-b px-4 py-2 rounded-tl-[15px] text-center text-xxs md:text-xxs lg:text-base">
                    DAPUR
                  </th>
                  <th className="border-b px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                    FREKUENSI
                  </th> */}
                  <th className="border-b px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                    No.
                  </th>
                  <th className="border-b px-4 py-2 text-left text-xxs md:text-xxs lg:text-base">
                    BAHAN
                  </th>
                  {/* <th className="border-b px-4 py-2">TOTAL KEBUTUHAN</th> */}
                  <th className="border-b px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                    JUMLAH PESANAN
                  </th>
                  <th className="border-b px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                    HARGA PER UNIT
                  </th>
                  <th className="border-b px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                    HARGA SUBTOTAL
                  </th>
                  <th className="border-b px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                    HARGA TOTAL
                  </th>
                  <th className="border-b px-4 py-2 text-xxs md:text-xxs lg:text-base">
                    PEMASOK
                  </th>
                  <th className="border-b px-4 py-2 rounded-tr-[15px] text-xxs md:text-xxs lg:text-base">
                    UBAH
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const ingredientSet = new Set(); // Track displayed ingredient IDs
                  const orderQtyTotals = {}; // Track total order_qty for each ingredient_id

                  // Calculate total order_qty for each ingredient_id
                  items.forEach((item) => {
                    if (orderQtyTotals[item.ingredient_id]) {
                      orderQtyTotals[item.ingredient_id] += parseInt(
                        item.order_qty,
                        10,
                      );
                    } else {
                      orderQtyTotals[item.ingredient_id] = parseInt(
                        item.order_qty,
                        10,
                      );
                    }
                  });

                  return items.map((item, index) => {
                    const isDuplicate = ingredientSet.has(item.ingredient_id); // Check if this ingredient is a duplicate
                    if (!isDuplicate) {
                      ingredientSet.add(item.ingredient_id); // Mark this ingredient as displayed
                    }
                    return (
                      <tr
                        key={index}
                        className={
                          item.frequency_name === "Mingguan"
                            ? "border-top border-b border-[#B6BBC2] bg-[#E6DBDD]"
                            : "border-top border-b border-[#B6BBC2] bg-transparent"
                        }
                      >
                        {/* <td className="px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                          {item.kitchen_name}
                        </td> */}
                        {/* <td
                          className={
                            "px-4 py-2 text-center text-xxs md:text-xxs lg:text-base"
                          }
                        >
                          {/* {isDuplicate ? "" : item.frequency_name} 
                          {item.frequency_name}
                        </td> */}
                        <td className="px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">{index+1}</td>
                        <td className="px-4 py-2 text-left text-xxs md:text-xxs lg:text-base">
                          {/* {isDuplicate ? "" : } */}
                          {item.ingredient_name}
                        </td>
                        {/* Total Order Quantity Column */}

                        {/* <td
        className="px-4 py-2 text-center"
      >
        {isDuplicate ? "" : formatNumber(orderQtyTotals[item.ingredient_id])}
      </td> */}
                        <td className="px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                          {formatNumber(item.order_qty)} {item.uoq_code}
                        </td>
                        <td className="px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                          Rp. {formatNumber(item.price_unit)},-
                        </td>
                        <td className="px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                          Rp. {formatNumber(item.price_unit*item.order_qty)},-
                        </td>
                        {
                        !isDuplicate && (<td className="px-4 py-2 text-right text-xxs md:text-xxs lg:text-base">
                          Rp. {formatNumber(item.price_total)},-
                        </td>
                        )
                       }{ isDuplicate &&(<td className="text-right px-4">-</td>)
                      }
                  
                        <td className="px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                          {item.supplier_name ? (
                            item.supplier_name
                          ) : (
                            <span className="text-red-500">
                              Pemasok dibutuhkan
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center text-xxs md:text-xxs lg:text-base">
                          {
                            // !isDuplicate && ( // Only render the button for the first occurrence
                            <button
                              className="text-gray-500 p-2 rounded hover:text-red-400 text-xxs md:text-xxs lg:text-base"
                              onClick={() =>
                                handleEditClick(
                                  item.id,

                                  item.location_id,
                                  item.kitchen_id,
                                  item.ingredient_id,
                                  item.date,
                                  item.date,
                                )
                              }
                            >
                              <EditIcon className="w-4 h-4 md:w-4 md:h-4 lg:w-5 lg:h-5" />
                            </button>
                            // )
                          }
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        ))}
    </div>
  );
}
